import {
    useCallback,
    useState,
} from 'react'

const SKY_NET_ALL_TABLES_KEY = 'SkyNetAllDataGridsState'

/* A hook to manage multiple table states in a single localStorage entry.
 */
function useSkyDataGridLocalStorage<T>(key: string, defaultValue: T) {
    const [
        storedValue,
        setStoredValue,
    ] = useState<T>(() => {
        if (typeof window === 'undefined') return defaultValue
        try {
            const allTables = localStorage.getItem(SKY_NET_ALL_TABLES_KEY)

            if (!allTables) return defaultValue

            const allValues = JSON.parse(allTables) as Record<string, T>

            return allValues[key] ?? defaultValue
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`Error reading localStorage key “${SKY_NET_ALL_TABLES_KEY}”:`, error)
            return defaultValue
        }
    })

    const setSingleValue = (value: T) => {
        try {
            const allTables = localStorage.getItem(SKY_NET_ALL_TABLES_KEY)
            let allValues: Record<string, T> = {}

            if (allTables) {
                allValues = JSON.parse(allTables) as Record<string, T>
            }

            allValues[key] = {
                ...allValues[key] ?? {},
                ...value,
            }
            localStorage.setItem(SKY_NET_ALL_TABLES_KEY, JSON.stringify(allValues))
            setStoredValue(value)
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`Error setting localStorage key “${SKY_NET_ALL_TABLES_KEY}”:`, error)
        }
    }

    const getStorageValue = useCallback(() => {
        if (typeof window === 'undefined') return defaultValue
        try {
            const allTables = localStorage.getItem(SKY_NET_ALL_TABLES_KEY)

            if (!allTables) return defaultValue

            const allValues = JSON.parse(allTables) as Record<string, T>

            return allValues[key] ?? defaultValue
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(`Error reading localStorage key “${SKY_NET_ALL_TABLES_KEY}”:`, error)
            return defaultValue
        }
    }, [
        defaultValue,
        key,
    ])

    return {
        dataGridState: storedValue,
        setDataGridState: setSingleValue,
        getStorageValue,
    }
}

export default useSkyDataGridLocalStorage
